import type { ConfirmDialogOptions } from '@ztechapps/common.hook'
import type { TemplateVariables } from '@ztechapps/chat.webui'
import { RequesterTemplateVariablesProvider } from '@ztechapps/chat.webui'
import type { ConversationTemplateVariablesProviderProps } from '@ztechapps/webui.contract'
import { CheckoutProvider, useCheckoutContext } from '@ztechapps/order.webui'
import { usePurchaseOrderDocument } from '@ztechapps/purchase-order.common'
import { useLogisticDocument } from '@ztechapps/logistic.common'
import { appSettings } from '../../config/app-settings'
import { useCheckoutReturnUrls } from '../../view/sales/item/hooks/useCheckoutReturnUrls'
import { usePurchaseOrderStatusEditable } from '../../view/purchases/item/hooks/usePurchaseOrderStatusEditable'
import { useSalesOrderTemplateVariables } from './template-helpers/useSalesOrderTemplateVariables'
import { usePurchaseOrderTemplateVariables } from './template-helpers/usePurchaseOrderTemplateVariables'
import { useLogisticTemplateVariables } from './template-helpers/useLogisticTemplateVariables'

type ConversationChildren = ConversationTemplateVariablesProviderProps<
  TemplateVariables,
  Record<any, Partial<ConfirmDialogOptions>>
>['children']

const SalesOrderTemplateVariablesReader = ({ children }: { children: ConversationChildren }) => {
  const { orderDocumentResult } = useCheckoutContext()
  const templateVariables = useSalesOrderTemplateVariables({ order: orderDocumentResult.document })

  return children({ templateVariables })
}

const SalesOrderTemplateVariables = ({ orderId, children }: { orderId: string; children: ConversationChildren }) => {
  const returnUrls = useCheckoutReturnUrls({ orderId })

  return (
    <CheckoutProvider orderId={orderId} appSettings={appSettings} {...returnUrls}>
      <SalesOrderTemplateVariablesReader>{children}</SalesOrderTemplateVariablesReader>
    </CheckoutProvider>
  )
}

const PurchaseOrderTemplateVariables = ({
  purchaseOrderId,
  children,
}: {
  purchaseOrderId: string
  children: ConversationChildren
}) => {
  const { document: purchaseOrder } = usePurchaseOrderDocument({ _id: purchaseOrderId })
  const hasRoleAndNotReceived = usePurchaseOrderStatusEditable({ purchaseOrderId, purchaseOrder })
  const { templateVariables, templateVariableConfirmDialogConfigs } = usePurchaseOrderTemplateVariables({
    purchaseOrder,
    hasRoleAndNotReceived,
  })

  return children({ templateVariables, templateVariableConfirmDialogConfigs })
}

const LogisticTemplateVariables = ({
  logisticId,
  children,
}: {
  logisticId: string
  children: ConversationChildren
}) => {
  const { document: logistic } = useLogisticDocument({ _id: logisticId })
  const templateVariables = useLogisticTemplateVariables({ logistic })

  return children({ templateVariables })
}

export const ConversationTemplateVariablesProvider = ({
  chat,
  children,
}: ConversationTemplateVariablesProviderProps<TemplateVariables, Record<any, Partial<ConfirmDialogOptions>>>) => {
  // `useDocument` fires even for an empty-string `_id`, so a record branch is taken only for a
  // non-empty id — anything else falls through to the requester.
  if (chat?.metadata?.orderId) {
    return <SalesOrderTemplateVariables orderId={chat.metadata.orderId}>{children}</SalesOrderTemplateVariables>
  }
  if (chat?.metadata?.purchaseOrderId) {
    return (
      <PurchaseOrderTemplateVariables purchaseOrderId={chat.metadata.purchaseOrderId}>
        {children}
      </PurchaseOrderTemplateVariables>
    )
  }
  if (chat?.metadata?.logisticId) {
    return <LogisticTemplateVariables logisticId={chat.metadata.logisticId}>{children}</LogisticTemplateVariables>
  }
  // The requester provider's confirm-configs default is wider than this provider's, so the render
  // prop crosses the boundary re-typed — only `templateVariables` flows through the fallback.
  const requesterChildren = children as ConversationTemplateVariablesProviderProps<TemplateVariables>['children']
  return <RequesterTemplateVariablesProvider chat={chat}>{requesterChildren}</RequesterTemplateVariablesProvider>
}
